import React from 'react';
import styled from 'styled-components';
import { theme, neutral, blueGrey, jadeGreen } from '../../design-system/theme';

interface TemplatePreviewProps {
  consentText: string;
  templateName?: string;
  dataCategories?: string[];
  description?: string;
  expiryDays?: number;
  evidenceTypes?: string[];
}

const PreviewContainer = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: white;
  min-height: 100%;
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${blueGrey[9]} 0%, ${blueGrey[8]} 100%);
  color: white;
  padding: 20px;
  text-align: center;
`;

const Logo = styled.div`
  width: 48px;
  height: 48px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin: 0 auto 12px;
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const HeaderSubtitle = styled.p`
  font-size: 13px;
  opacity: 0.8;
  margin: 4px 0 0;
`;

const ProgressBar = styled.div`
  display: flex;
  gap: 4px;
  padding: 16px 20px;
  background: ${blueGrey[1]};
  border-bottom: 1px solid ${neutral[2]};
`;

const ProgressStep = styled.div<{ $active?: boolean; $completed?: boolean }>`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: ${props => 
    props.$completed ? jadeGreen[5] : 
    props.$active ? theme.primary : 
    neutral[3]
  };
`;

const ContentSection = styled.div`
  padding: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${theme.textPrimary};
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ContentCard = styled.div`
  background: ${blueGrey[1]};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
`;

const ConsentContent = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${theme.textPrimary};
  
  h1, h2, h3, h4 {
    margin-top: 0;
    margin-bottom: 12px;
    color: ${theme.textPrimary};
  }
  
  h1 { font-size: 18px; }
  h2 { font-size: 16px; }
  h3 { font-size: 15px; }
  
  p {
    margin: 0 0 12px;
  }
  
  ul, ol {
    margin: 0 0 12px;
    padding-left: 20px;
  }
  
  li {
    margin-bottom: 4px;
  }
`;

const DataCategoryGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const DataCategoryTag = styled.div`
  background: white;
  border: 1px solid ${neutral[3]};
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CheckIcon = styled.span`
  color: ${jadeGreen[5]};
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: ${blueGrey[1]};
  border-radius: 8px;
  margin-bottom: 12px;
`;

const Checkbox = styled.div<{ $checked?: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.$checked ? jadeGreen[5] : neutral[4]};
  border-radius: 4px;
  background: ${props => props.$checked ? jadeGreen[5] : 'white'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  font-size: 12px;
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  color: ${theme.textPrimary};
  line-height: 1.4;
`;

const EvidenceSection = styled.div`
  margin-top: 20px;
`;

const EvidenceCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: white;
  border: 1px solid ${neutral[3]};
  border-radius: 12px;
  margin-bottom: 8px;
`;

const EvidenceIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${blueGrey[1]};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const EvidenceInfo = styled.div`
  flex: 1;
`;

const EvidenceName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${theme.textPrimary};
`;

const EvidenceDesc = styled.div`
  font-size: 12px;
  color: ${theme.textSecondary};
`;

const ButtonRow = styled.div`
  padding: 20px;
  display: flex;
  gap: 12px;
  background: white;
  border-top: 1px solid ${neutral[2]};
`;

const Button = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 14px 20px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$primary ? `
    background: ${theme.primary};
    color: white;
    border: none;
    
    &:hover {
      filter: brightness(1.05);
    }
  ` : `
    background: white;
    color: ${theme.textPrimary};
    border: 1px solid ${neutral[3]};
    
    &:hover {
      background: ${neutral[1]};
    }
  `}
`;

const getEvidenceIcon = (type: string): string => {
  const t = type.toLowerCase().replace('_', '-');
  if (t.includes('signature')) return '✍️';
  if (t.includes('phone') || t.includes('sms')) return '📱';
  if (t.includes('email')) return '📧';
  if (t.includes('video')) return '🎥';
  if (t.includes('audio')) return '🎤';
  return '🔐';
};

const getEvidenceName = (type: string): string => {
  const t = type.toLowerCase().replace(/_/g, ' ').replace(/-/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
};

export function TemplatePreview({
  consentText,
  templateName = 'Consent Form',
  dataCategories = [],
  description,
  expiryDays = 365,
  evidenceTypes = ['signature'],
}: TemplatePreviewProps) {
  return (
    <PreviewContainer>
      <Header>
        <Logo>🛡️</Logo>
        <HeaderTitle>{templateName}</HeaderTitle>
        {description && <HeaderSubtitle>{description}</HeaderSubtitle>}
      </Header>
      
      <ProgressBar>
        <ProgressStep $completed />
        <ProgressStep $active />
        <ProgressStep />
        <ProgressStep />
      </ProgressBar>
      
      <ContentSection>
        <SectionTitle>📄 Consent Details</SectionTitle>
        
        <ContentCard>
          <ConsentContent 
            dangerouslySetInnerHTML={{ __html: consentText || '<p>No consent content available</p>' }} 
          />
        </ContentCard>
        
        {dataCategories.length > 0 && (
          <>
            <SectionTitle>📊 Data Categories</SectionTitle>
            <DataCategoryGrid>
              {dataCategories.map((cat, i) => (
                <DataCategoryTag key={i}>
                  <CheckIcon>✓</CheckIcon>
                  {cat}
                </DataCategoryTag>
              ))}
            </DataCategoryGrid>
          </>
        )}
        
        <div style={{ marginTop: 20 }}>
          <CheckboxRow>
            <Checkbox $checked>✓</Checkbox>
            <CheckboxLabel>
              I confirm I have read / heard and understood the above
            </CheckboxLabel>
          </CheckboxRow>
          <CheckboxRow>
            <Checkbox />
            <CheckboxLabel>
              I agree to the collection and processing of my data for the specified purposes
            </CheckboxLabel>
          </CheckboxRow>
        </div>
        
        {evidenceTypes && evidenceTypes.length > 0 && (
          <EvidenceSection>
            <SectionTitle>🔐 Verification Required</SectionTitle>
            {evidenceTypes.map((type, i) => (
              <EvidenceCard key={i}>
                <EvidenceIcon>{getEvidenceIcon(type)}</EvidenceIcon>
                <EvidenceInfo>
                  <EvidenceName>{getEvidenceName(type)}</EvidenceName>
                  <EvidenceDesc>Required to complete consent</EvidenceDesc>
                </EvidenceInfo>
              </EvidenceCard>
            ))}
          </EvidenceSection>
        )}
        
        <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 16 }}>
          ⏱️ This consent is valid for {expiryDays} days from the date of submission.
        </div>
      </ContentSection>
      
      <ButtonRow>
        <Button>Cancel</Button>
        <Button $primary>Continue →</Button>
      </ButtonRow>
    </PreviewContainer>
  );
}

export default TemplatePreview;
